(() => {
  const PRODUCT_SELECTOR = '[data-drift-product]';
  const VIDEO_SELECTOR =
    '[data-drift-product-video], .drift-product__background-video';

  const VIDEO_TRIGGER_RATIO = 0.50;
  const MOTION_TRIGGER_RATIO = 0.65;

  const reducedMotion =
    window.matchMedia?.('(prefers-reduced-motion: reduce)') || {
      matches: false
    };


  /* ============================================================
     SHARED HELPERS
     ============================================================ */

  const getVisibleRatio = (section) => {
    const rect = section.getBoundingClientRect();

    const viewportHeight =
      window.innerHeight ||
      document.documentElement.clientHeight ||
      1;

    const visibleTop =
      Math.max(0, rect.top);

    const visibleBottom =
      Math.min(viewportHeight, rect.bottom);

    const visibleHeight =
      Math.max(
        0,
        visibleBottom - visibleTop
      );

    const referenceHeight =
      Math.max(
        1,
        Math.min(rect.height, viewportHeight)
      );

    return visibleHeight / referenceHeight;
  };


  /* ============================================================
     PRODUCT BACKGROUND VIDEO
     - starts once section reaches 50% visibility
     - fades over the fallback/poster when playback starts
     - plays ONCE only
     - remains on the final frame after ending
     - does not replay when scrolling back
     ============================================================ */

  const initProductVideo = (section) => {
    if (
      !section ||
      section.dataset.driftProductVideoInitialized === 'true'
    ) {
      return;
    }

    const video =
      section.querySelector(VIDEO_SELECTOR);

    if (!video) return;

    section.dataset.driftProductVideoInitialized = 'true';

    let started = false;
    let finished = false;
    let triggerObserver = null;
    let ticking = false;


    /*
      Keep the required autoplay-safe properties on the DOM property
      as well as the Liquid attributes. Safari is happier when these
      are set before play() is requested.
    */
    video.muted = true;
    video.defaultMuted = true;
    video.loop = false;
    video.playsInline = true;

    video.setAttribute(
      'muted',
      ''
    );

    /*
      Liquid may still contain a historical loop attribute.
      Remove it before playback so the product environment video
      always runs exactly once.
    */
    video.removeAttribute(
      'loop'
    );

    video.setAttribute(
      'playsinline',
      ''
    );


    const revealVideo = () => {
      video.dataset.driftVideoStarted = 'true';

      video.classList.add(
        'is-ready'
      );
    };


    const requestPlay = async () => {
      if (finished) return false;

      video.muted = true;
      video.defaultMuted = true;
      video.loop = false;
      video.playsInline = true;

      video.removeAttribute(
        'loop'
      );

      /*
        If metadata has not been fetched yet, explicitly begin loading
        when the section reaches its trigger point.
      */
      if (
        video.readyState === 0
      ) {
        try {
          video.load();
        } catch (_) {}
      }

      try {
        await video.play();

        /*
          play() resolving means the browser accepted playback.
          'playing' below will also call this, but doing it here avoids
          any visible delay caused by event scheduling.
        */
        revealVideo();

        return true;
      } catch (_) {
        return false;
      }
    };


    const startVideo = async () => {
      if (started || finished) return;

      started = true;

      video.dataset.driftVideoStarted = 'true';

      try {
        video.currentTime = 0;
      } catch (_) {}

      const played =
        await requestPlay();

      /*
        If play() happened before enough media was available, retry once
        the browser reports that it can actually play the file.
      */
      if (!played) {
        const retry = () => {
          requestPlay();
        };

        video.addEventListener(
          'canplay',
          retry,
          { once: true }
        );
      }
    };


    const handlePlaying = () => {
      if (!started) return;

      revealVideo();
    };


    const handleCanPlay = () => {
      if (
        started &&
        !finished &&
        video.paused &&
        !video.ended
      ) {
        requestPlay();
      }
    };


    /*
      Play-once behavior:
      mark the video complete and leave it on its final frame.
      Do not reset currentTime and do not call play() again.
    */
    const handleEnded = () => {
      if (!started) return;

      finished = true;

      video.dataset.driftVideoFinished = 'true';

      triggerObserver?.disconnect();
      triggerObserver = null;
    };


    const handlePageShow = () => {
      if (
        started &&
        !finished &&
        video.paused &&
        !video.ended
      ) {
        requestPlay();
      } else if (!started) {
        requestSafetyCheck();
      }
    };


    const handleVisibilityChange = () => {
      if (
        !document.hidden &&
        started &&
        !finished &&
        video.paused &&
        !video.ended
      ) {
        requestPlay();
      } else if (
        !document.hidden &&
        !started
      ) {
        requestSafetyCheck();
      }
    };


    /*
      Before the trigger, the fallback image / video poster remains visible.
    */
    try {
      video.pause();
    } catch (_) {}


    video.addEventListener(
      'playing',
      handlePlaying
    );

    video.addEventListener(
      'canplay',
      handleCanPlay
    );

    video.addEventListener(
      'ended',
      handleEnded
    );


    const safetyCheck = () => {
      ticking = false;

      const ratio =
        getVisibleRatio(section);

      if (
        !started &&
        ratio >= VIDEO_TRIGGER_RATIO
      ) {
        startVideo();

        return;
      }

      /*
        If the browser temporarily suspends the video before it finishes,
        resume it when visible again. Once `ended` fires, it stays finished.
      */
      if (
        started &&
        !finished &&
        ratio > 0 &&
        video.paused &&
        !video.ended
      ) {
        requestPlay();
      }
    };


    function requestSafetyCheck() {
      if (ticking) return;

      ticking = true;

      requestAnimationFrame(
        safetyCheck
      );
    }


    if (
      'IntersectionObserver' in window
    ) {
      triggerObserver =
        new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              const ratio =
                entry.intersectionRatio;

              if (
                !started &&
                entry.isIntersecting &&
                ratio >= VIDEO_TRIGGER_RATIO
              ) {
                startVideo();

                continue;
              }

              if (
                started &&
                !finished &&
                entry.isIntersecting &&
                video.paused &&
                !video.ended
              ) {
                requestPlay();
              }
            }
          },
          {
            threshold: [
              0,
              0.25,
              0.5,
              0.75,
              1
            ]
          }
        );

      triggerObserver.observe(section);
    } else {
      startVideo();
    }


    /*
      Geometry fallback for fast trackpad / bfcache cases.
    */
    window.addEventListener(
      'scroll',
      requestSafetyCheck,
      { passive: true }
    );

    window.addEventListener(
      'resize',
      requestSafetyCheck
    );

    window.addEventListener(
      'pageshow',
      handlePageShow
    );

    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );


    requestSafetyCheck();

    requestAnimationFrame(
      requestSafetyCheck
    );


    section.__driftProductVideoCleanup = () => {
      triggerObserver?.disconnect();

      window.removeEventListener(
        'scroll',
        requestSafetyCheck
      );

      window.removeEventListener(
        'resize',
        requestSafetyCheck
      );

      window.removeEventListener(
        'pageshow',
        handlePageShow
      );

      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );

      video.removeEventListener(
        'playing',
        handlePlaying
      );

      video.removeEventListener(
        'canplay',
        handleCanPlay
      );

      video.removeEventListener(
        'ended',
        handleEnded
      );



      delete video.dataset.driftVideoFinished;
      delete section.dataset.driftProductVideoInitialized;
    };
  };


  /* ============================================================
     PRODUCT ENTRANCE CHOREOGRAPHY
     Preserve approved v164 timing.
     Trigger ONCE at 65% visibility.
     ============================================================ */

  const initProductMotion = (section) => {
    if (
      !section ||
      section.dataset.driftProductMotionInitialized === 'true'
    ) {
      return;
    }

    section.dataset.driftProductMotionInitialized = 'true';


    if (
      reducedMotion.matches ||
      !('IntersectionObserver' in window)
    ) {
      section.classList.add(
        'is-visible'
      );

      return;
    }


    section.classList.add(
      'is-motion-ready'
    );


    let revealed = false;
    let observer = null;
    let ticking = false;


    const cleanup = () => {
      observer?.disconnect();

      window.removeEventListener(
        'scroll',
        requestSafetyCheck
      );

      window.removeEventListener(
        'resize',
        requestSafetyCheck
      );

      window.removeEventListener(
        'pageshow',
        requestSafetyCheck
      );

      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );
    };


    const reveal = () => {
      if (revealed) return;

      revealed = true;

      section.classList.add(
        'is-visible'
      );

      cleanup();
    };


    const safetyCheck = () => {
      ticking = false;

      if (
        !revealed &&
        getVisibleRatio(section) >= MOTION_TRIGGER_RATIO
      ) {
        reveal();
      }
    };


    function requestSafetyCheck() {
      if (
        revealed ||
        ticking
      ) {
        return;
      }

      ticking = true;

      requestAnimationFrame(
        safetyCheck
      );
    }


    function handleVisibilityChange() {
      if (!document.hidden) {
        requestSafetyCheck();
      }
    }


    observer =
      new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (
              !revealed &&
              entry.isIntersecting &&
              entry.intersectionRatio >= MOTION_TRIGGER_RATIO
            ) {
              reveal();

              break;
            }
          }
        },
        {
          threshold: [
            0,
            0.25,
            0.5,
            0.6,
            0.65,
            0.7,
            0.8,
            0.9,
            1
          ]
        }
      );


    observer.observe(section);


    window.addEventListener(
      'scroll',
      requestSafetyCheck,
      { passive: true }
    );

    window.addEventListener(
      'resize',
      requestSafetyCheck
    );

    window.addEventListener(
      'pageshow',
      requestSafetyCheck
    );

    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );


    requestSafetyCheck();

    requestAnimationFrame(
      requestSafetyCheck
    );


    section.__driftProductMotionCleanup = () => {
      cleanup();

      delete section.dataset.driftProductMotionInitialized;
    };
  };


  /* ============================================================
     INITIALIZATION
     ============================================================ */

  const initProduct = (section) => {
    initProductVideo(section);
    initProductMotion(section);
  };


  const initAll = (root = document) => {
    if (
      root.matches?.(PRODUCT_SELECTOR)
    ) {
      initProduct(root);
    }

    root
      .querySelectorAll?.(PRODUCT_SELECTOR)
      .forEach(
        initProduct
      );
  };


  const cleanupRoot = (root) => {
    const sections = [];


    if (
      root.matches?.(PRODUCT_SELECTOR)
    ) {
      sections.push(root);
    }


    root
      .querySelectorAll?.(PRODUCT_SELECTOR)
      .forEach(
        (section) => sections.push(section)
      );


    sections.forEach(
      (section) => {
        section.__driftProductVideoCleanup?.();
        section.__driftProductMotionCleanup?.();
      }
    );
  };


  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => initAll(),
      { once: true }
    );
  } else {
    initAll();
  }


  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAll(event.target);
    }
  );


  document.addEventListener(
    'shopify:section:select',
    (event) => {
      initAll(event.target);
    }
  );


  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      cleanupRoot(event.target);
    }
  );
})();
